import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contestsApi } from "@/api/contests";
import type { ContestPayload } from "@/types/contests";

export const contestKeys = {
  all: ["contests"] as const,
  list: () => [...contestKeys.all, "list"] as const,
  detail: (id: string) => [...contestKeys.all, "detail", id] as const,
  options: () => [...contestKeys.all, "options"] as const,
};

export function useContests() {
  return useQuery({
    queryKey: contestKeys.list(),
    queryFn: () => contestsApi.getContests(),
    staleTime: 60 * 1000,
  });
}

export function useContest(id: string | null) {
  return useQuery({
    queryKey: contestKeys.detail(id ?? ""),
    queryFn: () => contestsApi.getContest(id ?? ""),
    enabled: id !== null,
    staleTime: 60 * 1000,
  });
}

/** Insurers, product names and agents to pick from — partners only. */
export function useContestOptions(enabled: boolean) {
  return useQuery({
    queryKey: contestKeys.options(),
    queryFn: () => contestsApi.getOptions(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidating<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contestKeys.all });
    },
  });
}

/** `id` null creates a new contest (as a draft). */
export function useSaveContest() {
  return useInvalidating(
    ({ id, payload }: { id: string | null; payload: ContestPayload }) =>
      id === null
        ? contestsApi.createContest(payload)
        : contestsApi.updateContest(id, payload),
  );
}

export function useActivateContest() {
  return useInvalidating((id: string) => contestsApi.activateContest(id));
}

export function useFinishContest() {
  return useInvalidating((id: string) => contestsApi.finishContest(id));
}

export function useDeleteContest() {
  return useInvalidating((id: string) => contestsApi.deleteContest(id));
}
